import { AuthUser } from "@app/auth/model/auth-user";
import { AuthSelectors } from "@app/auth/store/selectors";
import { EntityWithAncestors } from "@app/entity/services/entity-db.service";
import { ApiResultState } from "@app/root-store/api-result.state";
import * as RouterSelectors from "@app/root-store/selectors/root-store.selectors";
import { ClareDbCollection } from "@app/shared/resources/resource-objectifier";
import { createFeatureSelector, createSelector } from "@ngrx/store";
import { EntityState, initialEntityState } from "../entity.state";

const selectState = createFeatureSelector<EntityState>("entity");

export const getEntitiesCallState = createSelector(
    selectState,
    (entityState: EntityState = initialEntityState) => entityState.entities.callState
);

export const selectEntities = createSelector(
    selectState,
    (entityState: EntityState = initialEntityState): ClareDbCollection<EntityWithAncestors> => entityState.entities.result
);

export const selectEntitiesArray = createSelector(selectState, (entityState: EntityState = initialEntityState): EntityWithAncestors[] => {
    if (entityState.entities?.result) {
        return Object.values(entityState.entities.result).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }
    return [];
});

export const selectEntitiesState = createSelector(
    selectState,
    (entityState: EntityState = initialEntityState): ApiResultState<ClareDbCollection<EntityWithAncestors>> => entityState.entities
);

export const loadingEntitiesState = createSelector(
    selectEntitiesState,
    (entityState: ApiResultState<ClareDbCollection<EntityWithAncestors>>) => entityState.callState
);

export const getSubEntities = createSelector(AuthSelectors.selectGetUser, selectEntitiesArray, (user, entities: EntityWithAncestors[]) =>
    user?.entityId && entities ? entities.filter(entity => entity.id !== user.entityId) : []
);

export const getNonSelfEntities = createSelector(RouterSelectors.findIdParam, selectEntitiesArray, (id, entities: EntityWithAncestors[]) =>
    entities.filter(entity => entity.id !== +id)
);

export const getNonSelfAndChildrenEntities = () =>
    createSelector(RouterSelectors.findIdParam, selectEntitiesArray, (id, entities: EntityWithAncestors[]) =>
        entities.filter(entity => entity.id !== +id && !entity.ancestors?.includes(+id))
    );

export const getEntity = (id: number) =>
    createSelector(selectEntities, (entities: ClareDbCollection<EntityWithAncestors>) => (entities ? entities[id] : undefined));

export const getCurrentEntity = createSelector(
    RouterSelectors.findIdParam,
    selectEntities,
    (entityId, entities: ClareDbCollection<EntityWithAncestors>) => {
        if (entityId) {
            return getEntity(+entityId).projector(entities);
        }
        return null;
    }
);

export const getCurrentEntityId = createSelector(RouterSelectors.findIdParam, entityId => +entityId);

export const getEntityIds = createSelector(selectEntities, (entities: ClareDbCollection<EntityWithAncestors>) => {
    const entityIds: number[] = [];
    if (entities) {
        Object.values(entities).forEach(entity => entityIds.push(entity.id));
    }

    return entityIds;
});

export const getChildEntitiesForId = (id: number) =>
    createSelector(selectState, (state: EntityState): EntityWithAncestors[] =>
        Object.values(state.entities.result).filter(entity => entity.parentId === id)
    );

export const getEntityList = createSelector(
    selectEntities,
    AuthSelectors.selectGetUser,
    getSubEntities,
    (entitiesObj: ClareDbCollection<EntityWithAncestors>, user: AuthUser, entities: EntityWithAncestors[]) =>
        entities
            .filter(entity => entity?.id !== user.entityId)
            .map(entity => {
                const parent = entitiesObj[entity.parentId] || {
                    name: undefined,
                };
                return {
                    ...entity,
                    parentName: parent.name,
                };
            })
);

export const getAuthUserEntity = createSelector(
    selectEntities,
    AuthSelectors.selectGetUser,
    (entities: ClareDbCollection<EntityWithAncestors> = [], authUser: AuthUser = null) =>
        entities && authUser ? entities[authUser.entityId] : null
);
